import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { RentalMiniCalendar } from '../components/rental/RentalMiniCalendar';
import { toast } from 'sonner';
import {
  Calendar, ArrowLeft, Loader2, Check, X, AlertTriangle, MapPin, Phone, Mail, User as UserIcon, ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (n) =>
  new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(n || 0);

const today = () => new Date().toISOString().slice(0, 10);

export default function RentalDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { isAuthenticated, token, user, loading: authLoading } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [availability, setAvailability] = useState(null); // {available: bool, conflicting_reservations: int}
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryStreet, setDeliveryStreet] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryPostal, setDeliveryPostal] = useState('');
  const [notes, setNotes] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  const lang = i18n.language;
  const name = product ? (lang === 'fr' ? product.name_fr : product.name_en) : '';
  const description = product ? (lang === 'fr' ? product.description_fr : product.description_en) : '';

  // Pre-fill customer info from authenticated user
  useEffect(() => {
    if (user) {
      setCustomerName((prev) => prev || user.name || '');
      setCustomerEmail((prev) => prev || user.email || '');
      setCustomerPhone((prev) => prev || user.phone || '');
    }
  }, [user]);

  // Fetch product
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/products/${productId}`);
        if (cancelled) return;
        if (res.data.department !== 'rental') {
          toast.error('Ce produit n\u2019est pas disponible à la location');
          navigate('/rental');
          return;
        }
        setProduct(res.data);
      } catch (err) {
        toast.error('Produit introuvable');
        navigate('/rental');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId, navigate]);

  // Compute days & totals
  const days = (() => {
    if (!startDate || !endDate) return 0;
    const s = new Date(startDate);
    const e = new Date(endDate);
    const ms = e.getTime() - s.getTime();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  })();
  const dailyRate = product?.rental_price_daily || (product ? product.price / 30 : 0);
  const subtotal = dailyRate * days;
  const taxes = subtotal * 0.14975;
  const total = subtotal + taxes;

  // Availability check (debounced)
  const checkAvailability = useCallback(async (s, e) => {
    if (!s || !e || new Date(e) <= new Date(s)) {
      setAvailability(null);
      return;
    }
    try {
      setChecking(true);
      const res = await axios.get(`${API}/rentals/availability/${productId}`, {
        params: {
          start_date: new Date(s + 'T00:00:00').toISOString(),
          end_date: new Date(e + 'T00:00:00').toISOString(),
        },
      });
      setAvailability(res.data);
    } catch (err) {
      setAvailability(null);
    } finally {
      setChecking(false);
    }
  }, [productId]);

  useEffect(() => {
    if (!startDate || !endDate) {
      setAvailability(null);
      return;
    }
    const tt = setTimeout(() => checkAvailability(startDate, endDate), 350);
    return () => clearTimeout(tt);
  }, [startDate, endDate, checkAvailability]);

  const handleCalendarDateClick = (iso) => {
    // First click sets start; second click (later date) sets end. Third click resets to new start.
    if (!startDate || (startDate && endDate)) {
      setStartDate(iso);
      setEndDate('');
      return;
    }
    // startDate set, no endDate yet
    if (iso < startDate) {
      setStartDate(iso);
      return;
    }
    setEndDate(iso);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour réserver');
      navigate('/login', { state: { from: `/rental/${productId}` } });
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Sélectionnez une date de début et de fin');
      return;
    }
    if (days < 1) {
      toast.error('La location doit être d\u2019au moins 1 jour');
      return;
    }
    if (availability && !availability.available) {
      toast.error('Ces dates ne sont pas disponibles');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        product_id: productId,
        start_date: new Date(startDate + 'T00:00:00').toISOString(),
        end_date: new Date(endDate + 'T00:00:00').toISOString(),
        notes: notes || undefined,
        customer_name: customerName || undefined,
        customer_email: customerEmail || undefined,
        customer_phone: customerPhone || undefined,
      };
      if (deliveryStreet || deliveryCity || deliveryPostal) {
        payload.delivery_address = {
          street: deliveryStreet,
          city: deliveryCity,
          postal_code: deliveryPostal,
        };
      }
      const res = await axios.post(`${API}/rentals`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Réservation créée avec succès!', {
        description: 'Rendez-vous dans votre compte pour signer le contrat.',
      });
      navigate(`/account?rental=${res.data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la réservation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-12 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Skeleton className="aspect-[4/3] w-full bg-gray-100" />
            <div className="space-y-4">
              <Skeleton className="h-8 w-2/3 bg-gray-100" />
              <Skeleton className="h-4 w-full bg-gray-100" />
              <Skeleton className="h-24 w-full bg-gray-100" />
              <Skeleton className="h-10 w-1/3 bg-gray-100" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const minDate = today();
  const minEndDate = startDate || minDate;

  return (
    <div className="min-h-screen bg-white" data-testid="rental-detail-page">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Link to="/rental">
          <Button variant="ghost" size="sm" className="text-gray-600 hover:text-black mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Retour aux locations
          </Button>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: product info */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="aspect-[4/3] overflow-hidden rounded-lg bg-gray-100 border border-gray-200 mb-4">
              <img
                src={
                  product.images?.[0] ||
                  'https://images.unsplash.com/photo-1612125033189-3a4950acc118?w=1200'
                }
                alt={name}
                className="w-full h-full object-cover"
              />
            </div>

            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2 font-mono">
              {product.category}
            </p>
            <h1 className="text-3xl font-bold text-black mb-3">{name}</h1>
            {description && <p className="text-gray-600 mb-4">{description}</p>}

            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-3xl font-bold text-black font-mono">
                {formatPrice(dailyRate)}
              </span>
              <span className="text-gray-500">/ jour</span>
            </div>

            {product.rental_price_weekly && (
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="border-gray-300 text-gray-700">
                  {formatPrice(product.rental_price_weekly)} / semaine
                </Badge>
                <Badge variant="outline" className="border-gray-300 text-gray-700">
                  {formatPrice(product.rental_price_monthly)} / mois
                </Badge>
              </div>
            )}

            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4 text-sm text-gray-700 space-y-2">
                <p className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 mt-0.5 text-green-600 shrink-0" />
                  <span>Paiement complet à la réservation (TPS + TVQ incluses)</span>
                </p>
                <p className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-green-600 shrink-0" />
                  <span>Contrat signé électroniquement après la réservation</span>
                </p>
                <p className="flex items-start gap-2">
                  <Phone className="h-4 w-4 mt-0.5 text-gray-500 shrink-0" />
                  <span>Support technique 24/7 inclus</span>
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right: reservation form */}
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="bg-white border-gray-200 shadow-sm sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-black mb-1 flex items-center gap-2">
                  <Calendar className="h-5 w-5" /> Réserver maintenant
                </h2>
                <p className="text-sm text-gray-500 mb-4">Vérification de disponibilité en temps réel</p>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="rental-form">
                  {/* Visual mini-calendar */}
                  <RentalMiniCalendar
                    productId={productId}
                    selectedStart={startDate}
                    selectedEnd={endDate}
                    onDateClick={handleCalendarDateClick}
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="start-date" className="text-gray-700 text-sm">Date de début *</Label>
                      <Input
                        id="start-date"
                        type="date"
                        value={startDate}
                        min={minDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="bg-white border-gray-300 text-black"
                        data-testid="rental-start-date"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="end-date" className="text-gray-700 text-sm">Date de fin *</Label>
                      <Input
                        id="end-date"
                        type="date"
                        value={endDate}
                        min={minEndDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="bg-white border-gray-300 text-black"
                        data-testid="rental-end-date"
                        required
                      />
                    </div>
                  </div>

                  {/* Availability indicator */}
                  {startDate && endDate && (
                    <div data-testid="availability-indicator">
                      {checking ? (
                        <div className="flex items-center gap-2 text-gray-500 text-sm p-2 bg-gray-50 rounded">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Vérification de la disponibilité...
                        </div>
                      ) : availability?.available ? (
                        <div className="flex items-center gap-2 text-green-700 text-sm p-2 bg-green-50 border border-green-200 rounded">
                          <Check className="h-4 w-4" />
                          Disponible pour ces dates
                        </div>
                      ) : availability && !availability.available ? (
                        <div className="flex items-start gap-2 text-red-700 text-sm p-2 bg-red-50 border border-red-200 rounded">
                          <X className="h-4 w-4 mt-0.5" />
                          <span>Non disponible — {availability.conflicting_reservations} réservation(s) en conflit pour ces dates.</span>
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* Totals */}
                  {days > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1 text-sm">
                      <div className="flex justify-between text-gray-700">
                        <span>{formatPrice(dailyRate)} × {days} jour{days > 1 ? 's' : ''}</span>
                        <span className="font-mono">{formatPrice(subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>Taxes (TPS + TVQ)</span>
                        <span className="font-mono">{formatPrice(taxes)}</span>
                      </div>
                      <Separator className="my-1" />
                      <div className="flex justify-between text-black font-semibold text-base">
                        <span>Total</span>
                        <span className="font-mono" data-testid="rental-total">{formatPrice(total)}</span>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Customer info */}
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-black flex items-center gap-2">
                      <UserIcon className="h-4 w-4" /> Vos coordonnées
                    </p>
                    {!isAuthenticated && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800 flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        Vous devez être connecté pour réserver. <Link to="/login" className="underline ml-1">Se connecter</Link>
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-3">
                      <Input
                        placeholder="Nom complet"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="bg-white border-gray-300 text-black"
                        data-testid="rental-customer-name"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="email"
                          placeholder="Courriel"
                          value={customerEmail}
                          onChange={(e) => setCustomerEmail(e.target.value)}
                          className="bg-white border-gray-300 text-black"
                          data-testid="rental-customer-email"
                        />
                        <Input
                          type="tel"
                          placeholder="Téléphone"
                          value={customerPhone}
                          onChange={(e) => setCustomerPhone(e.target.value)}
                          className="bg-white border-gray-300 text-black"
                          data-testid="rental-customer-phone"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Delivery */}
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-black flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> Lieu de livraison (facultatif)
                    </p>
                    <Input
                      placeholder="Adresse"
                      value={deliveryStreet}
                      onChange={(e) => setDeliveryStreet(e.target.value)}
                      className="bg-white border-gray-300 text-black"
                      data-testid="rental-delivery-street"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="Ville"
                        value={deliveryCity}
                        onChange={(e) => setDeliveryCity(e.target.value)}
                        className="bg-white border-gray-300 text-black"
                        data-testid="rental-delivery-city"
                      />
                      <Input
                        placeholder="Code postal"
                        value={deliveryPostal}
                        onChange={(e) => setDeliveryPostal(e.target.value)}
                        className="bg-white border-gray-300 text-black"
                        data-testid="rental-delivery-postal"
                      />
                    </div>
                  </div>

                  <Textarea
                    placeholder="Notes ou précisions (facultatif)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    className="bg-white border-gray-300 text-black"
                    data-testid="rental-notes"
                  />

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting || days < 1 || (availability && !availability.available) || checking}
                    className="w-full bg-black text-white hover:bg-gray-800 disabled:opacity-50"
                    data-testid="rental-submit"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Calendar className="h-4 w-4 mr-2" />
                    )}
                    {isAuthenticated ? 'Confirmer la réservation' : 'Se connecter pour réserver'}
                  </Button>

                  <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                    <Mail className="h-3 w-3" />
                    Vous recevrez une confirmation et le contrat à signer dans votre compte.
                  </p>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
